import type { MarkerClusterGroup, MarkerCluster } from 'leaflet'

export const useDbcLMarkerCluster = async () => {
  const L = window.L

  const options = {
    spiderfyOnMaxZoom: false,
    disableClusteringAtZoom: ZOOM_LEVEL_MAX_DISABLE_CLUSTERING,
    removeOutsideVisibleBounds: true,
    chunkedLoading: true,
    showCoverageOnHover: false,
    iconCreateFunction: clusterIconCreateFunction,
  }

  let markerCluster: MarkerClusterGroup

  if (import.meta.dev) {
    await import('leaflet.markercluster')
    const { MarkerClusterGroup } = await import('leaflet')
    markerCluster = new MarkerClusterGroup(options)
  } else {
    const { MarkerClusterGroup }: any = await import('leaflet.markercluster')
    markerCluster = new MarkerClusterGroup(options)
  }

  const addMarkers = (mcg: MarkerClusterGroup, locations: Availability[]) => {
    mcg.clearLayers()
    locations.forEach((location: Availability) => {
      const marker = L.marker([location.lat, location.lng], {
        title: location.name,
        icon: L.divIcon({
          html: createIconHtmlMarker(location),
          iconSize: new L.Point(40, 40),
        }),
      })

      marker.on('click', () => {
        const clickAvailability = new CustomEvent('availability', {
          detail: location,
          bubbles: true,
        })
        marker.options.icon?.options.html.dispatchEvent(clickAvailability)
      })

      mcg.addLayer(marker)
    })
  }

  return {
    markerCluster,
    addMarkers,
  }
}

const createIconHtmlCount = (count: string) => {
  const span = document.createElement('span')
  span.classList.add(
    'rounded-full',
    'absolute',
    'bg-white',
    'font-neo-screen-sans',
    'font-bold',
    'px-[4px]',
    'py-[2px]',
    'min-w-[1.4rem]',
    'text-center',
    'shadow-md',
    'bottom-full',
    'left-full',
    'translate-x-[-50%]',
    'translate-y-[50%]'
  )
  span.innerText = `${count}`
  return span
}

const createIconHtmlMarker = (location: Availability) => {
  const root = document.createElement('i')
  root.dataset.count = '1'
  root.dataset.provider = location.provider

  const image = document.createElement('img')
  image.classList.add('relative')
  image.setAttribute('src', createIconUrl(location))
  image.setAttribute('alt', location.name)
  root.appendChild(image)

  if (location.stationType === 'STATION' && location.bikes.length > 0) {
    const span = createIconHtmlCount(`${location.bikes.length}`)
    root.dataset.count = `${location.bikes.length}`
    root.appendChild(span)
  }

  return root
}

const createIconHtmlCluster = (count: string, provider: string) => {
  const root = document.createElement('i')
  root.classList.add('relative')

  const image = document.createElement('img')
  image.classList.add('relative')
  image.setAttribute('src', `/map/${provider}/cluster.svg`)
  root.appendChild(image)

  const span = createIconHtmlCount(count)
  root.appendChild(span)

  return root
}

const clusterIconCreateFunction = (cluster: MarkerCluster) => {
  const L = window.L
  const allChildMarkers = cluster.getAllChildMarkers()

  const providers = allChildMarkers.map(
    (marker) => marker.options.icon?.options.html.dataset.provider
  )

  const provider = determineMostOccuringProvider(providers)

  const countArray = allChildMarkers.map((marker) =>
    Number(marker.options.icon?.options.html.dataset.count)
  )

  const reducer = (accumulator: number, curr: number) => accumulator + curr

  const count = countArray.reduce(reducer)

  let newBikesCount =
    count <= 10
      ? count
      : count <= 100
      ? `+${Math.floor(count / 10) * 10}`
      : count <= 1000
      ? `+${Math.floor(count / 100) * 100}`
      : `+${Math.floor(count / 1000) * 1000}`

  if (count < 10) {
    newBikesCount = count
  }

  return new L.DivIcon({
    html: createIconHtmlCluster(`${newBikesCount}`, provider),
    className: newBikesCount === 0 ? 'hidden' : '',
    iconSize: new L.Point(40, 40),
  })
}
